import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  authService,
  type LoginRequest,
  type User,
} from '@/api/services/auth.service';
import { tokenManager } from '@/utils/tokenManager';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user on mount
  useEffect(() => {
    if (tokenManager.isAuthenticated()) {
      authService
        .getCurrentUser()
        .then(setUser)
        .catch(() => {
          tokenManager.clearTokens();
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (credentials: LoginRequest) => {
    const response = await authService.login(credentials);

    if (response.requires_2fa) {
      // Store session_id temporarily
      sessionStorage.setItem('2fa_session', response.session_id!);
      throw new Error('2FA_REQUIRED');
    }

    // Store tokens using tokenManager
    tokenManager.saveTokens(
      response.tokens.access_token,
      response.tokens.refresh_token,
      response.tokens.expires_in
    );

    // User is already in the response
    setUser(response.user);
  };

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      tokenManager.clearTokens();
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
